import { useState, useEffect, useCallback } from 'react';
import AdminService from '../../../../../services/adminService';
import EnhancedScoringEngine from '../../../../../services/enhancedScoringEngine';
import { normalizeSubmissionForDisplay, normalizeTimelineMilestones } from '../utils/normalizers';

/**
 * Custom hook for loading and managing submission data
 * Extracts data loading, scoring calculation, and normalization logic
 */
export const useSubmissionData = (submissionId) => {
    const [submission, setSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [scoringResults, setScoringResults] = useState(null);
    const [loadingScores, setLoadingScores] = useState(true);
    const [conversionTimeline, setConversionTimeline] = useState([]);
    const [leadActivities, setLeadActivities] = useState([]);
    const [communicationHistory, setCommunicationHistory] = useState([]);

    // Country code mapping for scoring engine
    const getCountryCodeForScoring = useCallback((selectedCountry) => {
        const countryMapping = {
            'germany': 'DEU',
            'canada': 'CAN',
            'australia': 'AUS',
            'uk': 'GBR',
            'usa': 'USA',
            'new zealand': 'NZL'
        };
        if (!selectedCountry) return 'DEU';
        return countryMapping[selectedCountry.toLowerCase()] || 'DEU';
    }, []);

    // Extract timeline from submission record
    const extractTimelineFromSubmission = useCallback((submissionRecord) => {
        if (!submissionRecord) return [];

        const stored =
            submissionRecord.cv_data?.conversionTimeline ||
            submissionRecord.conversionTimeline ||
            submissionRecord.conversion_timeline;

        if (Array.isArray(stored)) {
            return normalizeTimelineMilestones(stored);
        }

        if (stored?.milestones) {
            return normalizeTimelineMilestones(stored.milestones);
        }

        return [];
    }, []);

    // Calculate AI scoring
    const calculateScoring = useCallback(async (submissionData) => {
        try {
            setLoadingScores(true);

            const userProfile = {
                personalInfo: {
                    firstName: submissionData.first_name,
                    lastName: submissionData.last_name,
                    email: submissionData.email,
                    phone: submissionData.phone,
                    dateOfBirth: submissionData.personal_info?.dateOfBirth,
                    maritalStatus: submissionData.personal_info?.maritalStatus,
                    nationality: submissionData.personal_info?.nationality
                },
                education: submissionData.education_info || {},
                experience: submissionData.experience_info || [],
                languageSkills: submissionData.language_info || {},
                financialInfo: submissionData.financial_info || {},
                countrySelection: submissionData.country_selection || {}
            };

            const countryCode = getCountryCodeForScoring(submissionData.country_selection?.selectedCountry);

            const results = await EnhancedScoringEngine.calculateAllPathwayScores(
                userProfile,
                null,
                countryCode
            );

            setScoringResults(results);
            return results;
        } catch (err) {
            console.error('Error calculating scores:', err);
            return null;
        } finally {
            setLoadingScores(false);
        }
    }, [getCountryCodeForScoring]);

    // Load activities and communications
    const loadActivitiesAndCommunications = useCallback(async (id) => {
        try {
            const [activities, communications] = await Promise.all([
                AdminService.getLeadActivities(id),
                AdminService.getCommunicationHistory(id)
            ]);

            setLeadActivities(activities || []);
            setCommunicationHistory(communications || []);
        } catch (err) {
            console.error('Error loading activities and communications:', err);
        }
    }, []);

    // Main load function
    const loadSubmission = useCallback(async () => {
        if (!submissionId) return;

        try {
            setLoading(true);
            setError(null);

            const submissionData = await AdminService.getWizardSubmission(submissionId);
            const normalizedSubmission = normalizeSubmissionForDisplay(submissionData);
            setSubmission(normalizedSubmission);

            const storedTimeline = extractTimelineFromSubmission(submissionData);
            setConversionTimeline(storedTimeline);

            if (submissionData) {
                await calculateScoring(submissionData);
                await loadActivitiesAndCommunications(submissionData.id);
            }

            return normalizedSubmission;
        } catch (err) {
            console.error('Error loading submission:', err);
            setError('Failed to load submission details');
            return null;
        } finally {
            setLoading(false);
        }
    }, [submissionId, extractTimelineFromSubmission, calculateScoring, loadActivitiesAndCommunications]);

    // Reload function
    const reload = useCallback(() => {
        return loadSubmission();
    }, [loadSubmission]);

    // Load on mount and when ID changes
    useEffect(() => {
        loadSubmission();
    }, [loadSubmission]);

    return {
        // Data
        submission,
        scoringResults,
        conversionTimeline,
        leadActivities,
        communicationHistory,

        // State
        loading,
        loadingScores,
        error,

        // Actions
        reload,
        setSubmission,
        setConversionTimeline,
        setLeadActivities,
        calculateScoring,

        // Utilities
        getCountryCodeForScoring
    };
};

export default useSubmissionData;
